import { watch } from 'vue';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { i18n, t } from './i18n';
import { useSessionStore } from './stores/session';
/*
 * Static, and only these three — see the note on `routes`. They are in the entry's graph so Vite
 * preloads them from `index.html`, which is what removes the third round trip.
 */
import GameView from './views/GameView.vue';
import HomeView from './views/HomeView.vue';
import JoinView from './views/JoinView.vue';

/*
 * This augmentation has to live in a module that imports from vue-router — which this one does.
 * In a standalone .d.ts with no import, `declare module` becomes a global declaration instead
 * of an augmentation, and the only sign is `to.meta` typing as unknown again.
 */
declare module 'vue-router' {
  interface RouteMeta {
    /**
     * A message key, not a title: the tab is text on a screen like any other, and it is the one
     * piece nobody notices is in the wrong language until they have six tabs open. Translated
     * in `afterEach`, so it follows a language change on the very next navigation.
     */
    titleKey: string;
    /**
     * Needs a session of any kind, guest included — and there is deliberately no stronger gate
     * than this one. Hosting needs no verified email any more (docs/AUTH.md), so every screen
     * here is either open to anybody or open to anybody with a subject.
     *
     * `/host/new` is the interesting absence: it has no gate at all, because the form is where
     * somebody with no session gets one.
     */
    requiresSession?: boolean;
    /**
     * Wait for /me before rendering, without gating on the answer.
     *
     * The guard resolves the session on every navigation but *waits* for it on almost none —
     * holding the first paint behind a Lambda round trip is what put the landing page's CLS at
     * 1.0, and most screens either do not care who this is or have something honest to show
     * while they find out (the join screen says "checking"; settings offers the language and the
     * appearance, which need no account).
     *
     * This is for the ones that have neither. `/host/new` is the case: its first field exists
     * only for somebody with no session, so guessing renders a field that then vanishes, or
     * withholds one that then appears — a layout shift either way, and the wrong branch taken by
     * anybody who submits inside the window. A screen whose *shape* is the answer has nothing to
     * draw until it has one.
     *
     * Reaching it from the header costs nothing, which is how it is nearly always reached: the
     * session is long resolved by then and the wait is a resolved promise. Only a cold link
     * straight to one of these pays, and pays what every page used to.
     */
    needsSession?: boolean;
    /**
     * False on a screen that is handed to somebody else, where the header's own links are the
     * problem: Host and My games are one tap from the settings local play exists to keep a room
     * away from. Absent everywhere else — the shell is the default, and opting out is the
     * exception worth spelling.
     */
    chrome?: boolean;
  }
}

/*
 * Every route is lazy, so the first paint on a shared link is as small as possible — most
 * players arrive on a phone from a WhatsApp message.
 *
 * CloudFront rewrites unknown paths to /index.html, so a deep link like /game/K7M2QP loads
 * the app and this router takes it from there. See the backend's WebStack.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: { titleKey: 'titles.home' },
  },
  {
    path: '/join/:code?',
    name: 'join',
    // props, so the view is a function of its code rather than of the router — and so the
    // optional param arrives as an absent prop rather than an empty string.
    props: true,
    component: JoinView,
    meta: { titleKey: 'titles.join' },
  },
  {
    path: '/game/:code',
    name: 'game',
    props: true,
    component: GameView,
    meta: { titleKey: 'titles.game' },
  },
  {
    path: '/host/new',
    name: 'host-new',
    component: () => import('./views/CreateGameView.vue'),
    // No gate — somebody arriving here with no session types their name into the form and
    // continues as a guest, exactly the way the join screen works. But the form cannot be drawn
    // until we know whether that field belongs on it: see `needsSession`.
    meta: { titleKey: 'titles.hostNew', needsSession: true },
  },
  {
    path: '/host/:code',
    name: 'host-game',
    // props, so the view is a function of its code rather than of the router.
    props: true,
    component: () => import('./views/HostGameView.vue'),
    meta: { titleKey: 'titles.hostGame', requiresSession: true },
  },
  {
    // Under the game it belongs to, because that is where the host reaches it from and where
    // the link at the bottom of it goes back to.
    path: '/host/:code/local',
    name: 'local-play',
    props: true,
    component: () => import('./views/LocalPlayView.vue'),
    meta: { titleKey: 'titles.localPlay', requiresSession: true, chrome: false },
  },
  {
    path: '/games',
    name: 'my-games',
    component: () => import('./views/MyGamesView.vue'),
    meta: { titleKey: 'titles.myGames', requiresSession: true },
  },
  {
    /*
     * No gate at all, deliberately. The language choice lives here (docs/ROADMAP.md, Phase 9)
     * and most players in a WhatsApp pool never sign in — putting the app's own language behind
     * an account would be the wrong trade. The screen itself decides what it can offer.
     */
    path: '/settings',
    name: 'settings',
    component: () => import('./views/SettingsView.vue'),
    meta: { titleKey: 'titles.settings' },
  },
  {
    /*
     * No gate, and reachable from the footer of every screen: somebody deciding whether to type
     * their name into this thing has not signed in yet, and that is exactly when they want it.
     */
    path: '/privacy',
    name: 'privacy',
    component: () => import('./views/PrivacyView.vue'),
    meta: { titleKey: 'titles.privacy' },
  },
  {
    /*
     * No gate, and the same reason as /privacy carried to its conclusion: somebody who cannot
     * sign in is exactly who needs this, and a form behind a login is a form they cannot reach.
     */
    path: '/contact',
    name: 'contact',
    component: () => import('./views/ContactView.vue'),
    meta: { titleKey: 'titles.contact' },
  },
  {
    /*
     * No gate, for the same reason /privacy has none, and one more: this is the page written for
     * somebody who has not decided to play yet. docs/GAME_RULES.md is the specification, and it
     * lives in a private repository — so this route is the only public form the rules have.
     */
    path: '/how-to-play',
    name: 'how-to-play',
    component: () => import('./views/HowToPlayView.vue'),
    meta: { titleKey: 'titles.howToPlay' },
  },
  {
    path: '/sign-in',
    name: 'sign-in',
    component: () => import('./views/SignInView.vue'),
    meta: { titleKey: 'titles.signIn' },
  },
  {
    // Catch-all rather than a 404 page: a mistyped path is far more likely to be a mangled
    // game link than a genuine navigation error.
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('./views/NotFoundView.vue'),
    meta: { titleKey: 'titles.notFound' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

/*
 * The session is resolved here rather than in main.ts, for two reasons: main.ts is excluded
 * from coverage, so logic there is untested logic; and a /me call that fails before mount would
 * leave a blank page instead of an app that can say what went wrong.
 *
 * useSessionStore needs an active Pinia. main.ts installs Pinia before the router and before
 * mount, and no navigation happens until mount — so by the time this runs, it is there.
 */
router.beforeEach(async (to) => {
  const session = useSessionStore();

  /*
   * Started here, and *waited for* only by the screens with a gate to apply it to.
   *
   * This used to be awaited on every navigation, and it is what put the landing page's CLS at
   * 1.0 — ten times the "good" bar, on the URL carrying almost all of the traffic. Nothing
   * rendered until a Lambda round trip came back, so the shell painted with an empty `<main>`
   * and the whole page then arrived at once, shoving the footer down the height of it.
   *
   * The catch is attached where the call is started rather than where it is awaited, because on
   * an ungated route nothing awaits it: the store rethrows anything that is not a 401, and an
   * unhandled rejection out of a guard is a blank page. Waiting on it below then cannot throw,
   * which is the right shape anyway — a /me that failed and a /me that said "nobody" leave a
   * gate with the same answer.
   */
  const resolving = session.load().catch(() => {
    // Deliberately empty. `isSignedIn` is false either way, and that is all a gate reads.
  });

  // Two reasons to wait: a gate to apply, or a screen that cannot draw itself without the
  // answer. Only the first of them turns anybody away.
  if (to.meta.requiresSession || to.meta.needsSession) {
    await resolving;

    // The only gate. No session at all means nothing has ever been joined or hosted from this
    // browser, so a manage screen or a game list has genuinely nothing to show.
    if (to.meta.requiresSession && !session.isSignedIn) {
      return { name: 'sign-in', query: { redirect: to.fullPath } };
    }
  }

  return true;
});

const applyTitle = (): void => {
  document.title = t(router.currentRoute.value.meta.titleKey);
};

/**
 * The apex, always — even on dev.
 *
 * Hard-coded rather than read from `location`, which is the same trade index.html already makes
 * for og:image: dev.rugbysquares.com serves byte-identical content and is never the canonical
 * version of anything. This is the belt to the braces in the dev distribution — an
 * `X-Robots-Tag: noindex` response header, set per stage in apps/infra/lib/security-headers.ts.
 */
const SITE_ORIGIN = 'https://rugbysquares.com';

/*
 * The path only, deliberately: the query is dropped.
 *
 * Every campaign link arrives carrying `?utm_source=...`, and without this each variant is a
 * separate URL with identical content, so Google picks one to rank and splits the signals
 * between the rest. Reusing the existing tag rather than appending matters for the same reason
 * applyTitle runs on every navigation — this is a SPA, so nothing reloads the head.
 */
const applyCanonical = (): void => {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  const link = existing ?? document.head.appendChild(document.createElement('link'));

  link.rel = 'canonical';
  link.href = `${SITE_ORIGIN}${router.currentRoute.value.path}`;
};

router.afterEach(() => {
  applyTitle();
  applyCanonical();
});

/*
 * And again when the language changes, because nothing navigates then. Found by using it: the
 * settings screen switched to Afrikaans with "Settings" still in the tab, and it stayed there
 * until the next navigation.
 */
watch(i18n.global.locale, applyTitle);
